import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { isMobileOnly } from 'react-device-detect';
import moment from 'moment';
import { isEmpty } from 'lodash-es';
import queryString from 'query-string';
import {
  BaseBox,
  CenterBox,
  FlexBox,
  Heading1,
  Heading3,
  Heading4,
  Body1,
  Icons,
  Heading2,
} from '@docon/docon-ui-library';
import DocItem from 'components/Doctors/DocItem';
import CancelAppointment from './modal/CancelAppointment';
import { getAppointment } from 'redux/reducer/appointment';
import { headerKey, restClient } from 'services';
import { Share, Cancel } from 'shared/icons';
import { Loader, Image, GetDirection } from 'shared';
import { getAddressString, getTimeFromMinutes } from 'utils';
import { bgColor, borderColor, colors } from 'utilities/colorConfig';
import { emitEvent, isPeIosWebview } from 'utils/webview/webview';
import { useCookies } from 'react-cookie';
import { cookieNames } from 'utils/constants';

export enum STATUS {
  NO_Show = 'noshow',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  BOOKED = 'booked',
}

const actionLists = [
  { id: 'toCalendar', label: 'Add to calendar', Icon: Icons.CalenderIcon },
  { id: 'shareDetails', label: 'Share details', Icon: Share },
  { id: 'cancelAppointment', label: 'Cancel appointment', color: colors.error, Icon: Cancel },
];

const ZeroCase = styled(CenterBox)`
  height: 100%;
  padding: 16px;
  font-weight: 600;
`;

const CallCta = styled(Heading4)`
  height: 40px;
  width: 230px;
  margin: auto;
  font-weight: 500;
  text-align: center;
  line-height: 40px;
  color: ${colors.white};
  border-radius: 4px;
  cursor: pointer;
  transition: all .2s ease-in-out;

  &:hover {
    transform: scale(1.1);
    filter: brightness(90%);
  }
`;

const Row = styled(FlexBox)`
  padding: 3% 20px;
  gap: 0 30px;

  ${({ vertical }) => !vertical && css`
    text-align: left;
  `}

  & + & {
    border-top: 1px solid ${borderColor};
  }
`;

const InfoCard = styled(BaseBox)`
  margin: auto;
  background: ${colors.white};
  border-radius: 10px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);
`;

const ActionLists = styled(Heading3)`
  display: block;
  margin: 4% auto;
  color: ${colors.primaryBlue};

  li {
    display: flex;
    gap: 14px;
    align-items: center;
    padding: 3% 2%;
    cursor: pointer;
    border-bottom: 1px solid ${borderColor};
  }
`;

const Content = styled(BaseBox)`
  margin: auto;
  max-width: 500px;
  text-align: center;

  .headingMsg {
    max-width: 80%;
    margin: auto;
  }
`;

const Header = styled(FlexBox)`
  position: sticky;
  top: 0;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  background: linear-gradient(301.35deg, #1769d7 47.75%, #0089ff 100%);

  ${Image} {
    position: absolute;
    height: 100%;
    width: 100%;
    opacity: 0.2;
  }
  
  .wrapper {
    position: relative;    
    width: auto;
    margin: auto;
    padding: 12px 20px;
    text-align: center;
    color: ${colors.white};

    ${Body1} { text-align: right; }
  }
`;

const Root = styled(BaseBox)`
  height: 100%;
  min-height: -webkit-fill-available;
  display: flex;
  flex-direction: column;
  background-color: ${bgColor.base};

  ${() => isMobileOnly && css`
    ${Content} {
      margin: auto 20px;

      ${Row} {
        padding: 12px 20px;
        gap: 0 16px;
      }
    }
  `}
`;

const calenderUrl = 'http://www.google.com/calendar/event?';

function PatientConfirm() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { appointmentId } = useParams<any>();
  const location = useLocation();
  const searchParams = queryString.parse(location.search);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [cookies, setCookies] = useCookies([cookieNames.VENDOR_COOKIE]);
  const isPharmeasyIosWebview = isPeIosWebview(cookies[cookieNames.VENDOR_COOKIE], navigator?.userAgent);

  const {
    appointment,
    isFetchingAppointment,
    isAppointmentCanceled,
  } = useSelector(state => state.appointment);

  useEffect(() => {
    if (searchParams.tk) {
      restClient.setHeader(headerKey.AccessToken, searchParams.tk);
      dispatch(getAppointment({ appointmentId }));
    }
  }, [searchParams.tk, isAppointmentCanceled]);

  const {
    patientDetails: {
      firstName: pFirstName = '',
      lastName: pLastName = '',
      officeID = '',
    } = {},
    tmpUserDetails: {
      firstName: tmpUFirstName = '',
      lastName: tmpULastName = '',
    } = {},
    doctordetails: {
      firstName: dFirstName = '',
      lastName: dLastName = '',
    } = {},
    clinicdetails: {
      name: clinicName = '',
      number: clinicNumber = null,
      address: clinicAddress = {},
    } = {},
    appointmentTime,
    appointmentDisplayTime,
    appointmentDate,
    shortUrl,
    status: appointmentStatus,
  } = appointment || {};

  const patientFullName = `${pFirstName || ''} ${pLastName || ''}`;
  const docFullName = `${dFirstName || ''} ${dLastName || ''}`;
  const tmpUserFullName = `${tmpUFirstName || ''} ${tmpULastName || ''}`;

  function getCalenderUrl() {
    const date = appointmentDisplayTime || appointmentDate;
    const isoS = moment(date).format('YYYYMMDDTHHmmSS');
    const isoE = moment(date).add(30, 'minutes').format('YYYYMMDDTHHmmSS');

    const params = {
      action: 'TEMPLATE',
      text: `Appointment with Dr. ${docFullName}`,
      location: address,
      details: 'For Cosultation/follow-up',
      dates: `${isoS}/${isoE}`,
    };
    return `${calenderUrl}${queryString.stringify(params)}`;
  }

  function generateShareMsg() {
    let msg = `CONFIRMED ${patientFullName || tmpUserFullName} ${officeID ? `(${officeID})` : ''} appointment for `;

    msg = `${msg}
          ${moment(appointmentDate, 'YYYY-MM-DD HH:mm:ss ZZ').format('DD/MMM/YYYY')}
          ${moment().startOf('day').add(appointmentTime, 'minutes').format('hh:mm A')}
          with ${docFullName} at ${clinicName}, ${getAddressString(clinicAddress, true)}. Ph: ${clinicNumber}
           For Details ${shortUrl} Team DocOn!`;

    msg = 'https://api.whatsapp.com/send?text=' + encodeURI(msg);
    return msg;
  }

  function handleClick(event: any) {
    event.stopPropagation();
    const id = event?.currentTarget?.id;
    switch (id) {
      case 'toCalendar':
        if (isPharmeasyIosWebview) {
          const calUrl = getCalenderUrl();
          const deeplink = `push.pharmeasy.clevertap://deeplink/open_url_in_browser?url=${calUrl}`;
          emitEvent('onAction', {
            deeplink,
          });
        } else {
          window.open(getCalenderUrl(), '_blank');
        }
        break;
      case 'shareDetails':
        window.open(generateShareMsg(), '_blank');
        break;
      case 'cancelAppointment':
        setOpenCancelModal(true);
        break;
      case 'clinicNumber':
        if (isPharmeasyIosWebview) {
          const deeplink = `push.pharmeasy.clevertap://deeplink/open_dialer?phoneNumber=${clinicNumber}`;
          emitEvent('onAction', {
            deeplink,
          });
        } else {
          window.location.href = `tel: ${clinicNumber}`;
        }
        break;
    }
  }

  function handleDocItemClick({ shortUrl }: any) {
    history.push(`/doctors/${shortUrl}`);
  }

  function handleModalClose() {
    setOpenCancelModal(false);
  }

  if (isFetchingAppointment) return <Loader />;

  if (isEmpty(appointment)) {
    return (
      <ZeroCase>
        <Heading3 title={'Sorry No appointment Found'} />
      </ZeroCase>
    );
  }

  let heading = '';
  let icon = '';
  let callCtaBg = '';
  const picSize = isMobileOnly ? 45 : 54;
  const displayTime = getTimeFromMinutes(appointmentTime);
  const appointmentDisplayDate = moment(appointmentDate, 'YYYY-MM-DD HH:mm:ss ZZ').format('dddd, DD MMM YYYY');
  const address = getAddressString(clinicAddress);

  switch (appointmentStatus) {
    case STATUS.NO_Show:
      icon = '/images/calendar-orange.png';
      callCtaBg = '#ff9342';
      heading = `
      Hi, you missed your appointment with Dr. ${docFullName} on 
      ${displayTime}, ${appointmentDisplayDate}
      `;
      break;
    case STATUS.CANCELLED:
      icon = '/images/calendar-red.png';
      callCtaBg = '#ff6542';
      heading = `
          Your appointment was cancelled with Dr.${docFullName} on 
          ${displayTime}, ${appointmentDisplayDate}
        `;
      break;
    case STATUS.COMPLETED:
      icon = '/images/calendar-green.png';
      callCtaBg = '#00c35e';
      heading = `Thank You for visiting Dr. ${docFullName}.`;
      break;
  }

  function renderContent() {
    if (heading)
      return (
        <Content>
          <Heading4 className={'headingMsg'} fontWeight={500} title={heading} />
          <CenterBox py='10%'>
            <Image src={icon} alt='status icon' />
          </CenterBox>

          <CallCta
            id='clinicNumber'
            title={'Call for appointment'}
            bg={callCtaBg}
            onClick={handleClick}
          />
        </Content>
      );

    if (appointmentStatus === STATUS.BOOKED)
      return (
        <Content>
          <Heading2
            color={colors.secondaryColor}
            fontWeight={500}
            py={'20px'}
            title={`${patientFullName.trim() || tmpUserFullName} ${officeID ? `(${officeID})` : ''}`}
          />

          <InfoCard>
            <Row vertical verticalSpace={0}>
              <Heading1 fontWeight={600} title={displayTime} />
              <Heading3 title={appointmentDisplayDate} />
            </Row>
            <Row
              as={DocItem}
              showRightArrow
              picSize={`${picSize}px`}
              doctor={appointment.doctordetails}
              subTitle={appointment.speciality.name}
              onClick={handleDocItemClick}
            />
            <Row hidden={!address}>
              <Image size={`${picSize}px`} src={'/images/directions-big.png'} />
              <BaseBox flex={1}>
                <Heading4 title={address} />
                <GetDirection address={address} />
              </BaseBox>
            </Row>

            <Row hidden={!clinicNumber} id='clinicNumber' onClick={handleClick}>
              <CenterBox
                size={picSize}
                bg={colors.success}
                borderRadius={'50%'}
              >
                <Icons.PhoneIcon color={colors.white} />
              </CenterBox>
              <BaseBox flex={1}>
                <Heading3 fontWeight={500} title={'Call Clinic'} />
                <Heading4 title={clinicNumber} />
              </BaseBox>
            </Row>
          </InfoCard>

          <ActionLists tagAs='ul'>
            {
              actionLists.map(({ Icon, ...list }: any) => (
                <Heading3
                  key={list.id}
                  tagAs='li'
                  id={list.id}
                  color={list.color || 'currentColor'}
                  title={<><Icon height={22} /> {list.label}</>}
                  onClick={handleClick}
                />
              ))
            }
          </ActionLists>
        </Content>
      );

    return null;
  }

  return (
    <Root>
      <Header>
        <Image src='/images/clinic-demo.JPG' height={'100px'} />
        <BaseBox className='wrapper'>
          <Heading1 color='currentColor' fontWeight='bold' title={clinicName} />
          <Body1 color='currentColor' title={'Powered by docon'} />
        </BaseBox>
      </Header>

      {renderContent()}

      <CancelAppointment
        isOpen={openCancelModal}
        appointment={appointment}
        onClose={handleModalClose}
      />
    </Root>
  );
}

export default PatientConfirm;